import { createServerFn } from "@tanstack/react-start";
import type { ApiEnvelope, GiftCard, GiftCardDetail, Paged } from "@/lib/api";

/** Read-only bridge to the existing public API; no database or private credentials. */
export const readGiftCardCatalog = createServerFn({ method: "GET" })
  .inputValidator((input: { brand?: string; page?: number }) => {
    if (input.brand && input.brand.length > 200) throw new Error("Invalid brand");
    const page = input.page ?? 1;
    if (!Number.isSafeInteger(page) || page < 1) throw new Error("Invalid page");
    return { brand: input.brand, page };
  })
  .handler(async ({ data }) => {
    const params = new URLSearchParams({ page: String(data.page), limit: "100" });
    if (data.brand) params.set("brand", data.brand);
    const base = (process.env.VITE_API_BASE_URL || "https://nexkey.gg").replace(/\/$/, "");
    const response = await fetch(`${base}/api/gift-cards?${params}`, { signal: AbortSignal.timeout(15_000) });
    if (!response.ok) throw new Error("Gift cards could not be loaded");
    return await response.json() as ApiEnvelope<GiftCard[]> & Partial<Omit<Paged<GiftCard>, "data">>;
  });

export const readGiftCardProduct = createServerFn({ method: "GET" })
  .inputValidator((input: { id: string }) => {
    if (!/^[a-zA-Z0-9-]{1,100}$/.test(input.id)) throw new Error("Invalid gift card");
    return input;
  })
  .handler(async ({ data }) => {
    const base = (process.env.VITE_API_BASE_URL || "https://nexkey.gg").replace(/\/$/, "");
    const response = await fetch(`${base}/api/gift-cards/${encodeURIComponent(data.id)}`, { signal: AbortSignal.timeout(15_000) });
    if (!response.ok) throw new Error(response.status === 404 ? "Gift card not found" : "Gift card could not be loaded");
    return await response.json() as ApiEnvelope<GiftCardDetail>;
  });
